import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { isProductBundle } from '@/lib/product-utils';
import { cn } from '@/lib/utils';
import type { SCAPIShopperProductsSchemas } from '@/types';

type PrefixedProductCustomAttributes = {
    [K in keyof ProductCustomAttributes as K extends string ? `c_${K}` : never]?: ProductCustomAttributes[K];
};

export type ProductWithPimAttributes = SCAPIShopperProductsSchemas['Product'] & PrefixedProductCustomAttributes;

type ProductCustomAttributeKey = keyof PrefixedProductCustomAttributes;

type AccordionConfig = {
    id: string;
    label: string;
    keys: readonly ProductCustomAttributeKey[];
    requiredKey?: ProductCustomAttributeKey;
    showAttributeLabels?: boolean;
    bundleOnly?: boolean;
};

const PRODUCT_DETAILS_ACCORDIONS = [
    {
        id: 'receipt',
        label: 'tabs.details.receiptAndNutritionInfo',
        keys: [
            'c_pimInciQuality',
            'c_pimIngredientsQuality',
            'c_pimAllergensQuality',
            'c_pimGenericAllergens',
            'c_pimNutritionalInfoEU',
        ],
    },
    {
        id: 'usage',
        label: 'tabs.details.usageInstructions',
        keys: ['c_pimTipsForUse'],
    },
    {
        id: 'oil',
        label: 'tabs.details.oil',
        requiredKey: 'c_pimCultivarOil',
        showAttributeLabels: true,
        keys: [
            'c_pimCultivarOil',
            'c_pimCollectionMethod',
            'c_pimCrushingMethod',
            'c_pimOlfactoryNotes',
            'c_pimFruityIntensity',
            'c_pimColor',
        ],
    },
    {
        id: 'taste',
        label: 'tabs.details.taste',
        keys: ['c_pimTaste', 'c_pimWineTemperature', 'c_pimWinePairings'],
    },
    {
        id: 'basket',
        label: 'tabs.details.basket',
        showAttributeLabels: true,
        bundleOnly: true,
        keys: ['c_pimMeasure', 'c_pimGrossWeight', 'c_pimPiecesConf'],
    },
] satisfies readonly AccordionConfig[];

function formatProductAttributeValue(value: PrefixedProductCustomAttributes[ProductCustomAttributeKey]) {
    if (Array.isArray(value)) {
        return value.join(', ');
    }

    return String(value);
}

function getVisibleAccordions(product: ProductWithPimAttributes) {
    return PRODUCT_DETAILS_ACCORDIONS.filter((accordion) => {
        if (accordion.bundleOnly && !isProductBundle(product)) {
            return false;
        }

        if (accordion.requiredKey) {
            return Boolean(product[accordion.requiredKey]);
        }

        return accordion.keys.some((key) => product[key]);
    });
}

export function ProductDetailsAccordion({
    product,
    className,
}: {
    product: ProductWithPimAttributes;
    className?: string;
}) {
    const { t } = useTranslation('product');
    const accordions = useMemo(() => getVisibleAccordions(product), [product]);

    if (accordions.length === 0) {
        return null;
    }

    return (
        <Accordion type="multiple" className={cn('w-full', className)}>
            {accordions.map((accordion) => (
                <AccordionItem key={accordion.id} value={accordion.id} className="border-neutral-80">
                    <AccordionTrigger className="cursor-pointer py-6 text-left hover:no-underline">
                        {t(accordion.label as never)}
                    </AccordionTrigger>
                    <AccordionContent>
                        <dl className="space-y-4 text-neutral-10">
                            {accordion.keys.map((key) => {
                                const value = product[key];
                                if (!value) return null;
                                if (key === 'c_pimNutritionalInfoEU') {
                                    return (
                                        <div
                                            key={key}
                                            className="max-w-full overflow-x-auto overscroll-x-contain rounded bg-sand-95 px-4 py-4 text-base font-normal leading-6 tracking-[0.01em] [&_table]:w-max [&_table]:min-w-full [&_table]:border-collapse [&_tr]:border-b [&_tr]:border-neutral-80 [&_tbody_tr:last-child]:border-0 [&_th]:py-2 [&_th]:font-normal [&_td]:py-2 [&_thead_th]:w-px [&_thead_th]:whitespace-nowrap [&_thead_th]:pl-4 [&_thead_th]:text-right [&_thead_th:first-child]:w-auto [&_thead_th:first-child]:pl-0 [&_tbody_th]:pr-4 [&_tbody_th]:text-left [&_tbody_th]:text-neutral-40 [&_td]:w-px [&_td]:whitespace-nowrap [&_td]:pl-4 [&_td]:text-right [&_td]:text-neutral-10"
                                            // eslint-disable-next-line react/no-danger
                                            dangerouslySetInnerHTML={{
                                                __html: formatProductAttributeValue(value),
                                            }}
                                        />
                                    );
                                }
                                return (
                                    <div key={key} className="w-fit max-w-full gap-x-2">
                                        {accordion.showAttributeLabels ? (
                                            <dt className="body-copy-l-bold mr-2 inline break-words">
                                                {t(`pimLabelAttributes.${key}` as never)}
                                            </dt>
                                        ) : null}
                                        <dd
                                            className="body-copy-l inline"
                                            // eslint-disable-next-line react/no-danger
                                            dangerouslySetInnerHTML={{
                                                __html: formatProductAttributeValue(value),
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </dl>
                    </AccordionContent>
                </AccordionItem>
            ))}
        </Accordion>
    );
}
